import { Box, Divider, Stack, Typography } from '@mui/material';
import { CloudAccountType, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { NumericBulletSection } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/numericBulletSection';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useDAL } from '@/contexts/useDAL';

import { useAccountRoleName } from '../useAccountRoleName';
import { useCreateAwsAccount } from '../useCreateAwsAccount';
import { useValidateAccount } from '../validateAccount';

const accountIdConfig = getCloudFieldConfig(Provider.Aws, 'accountId');

export const ConnectAwsVaultAccountPanel = ({
  mutate,
  onClose,
}: {
  mutate: () => Promise<void>;
  onClose: () => void;
}) => {
  const dal = useDAL();
  const [openCloudFormationClicked, setOpenCloudFormationClicked] =
    useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isDeploying, setIsDeploying] = useState(false);
  const [roleName, setRoleName] = useAccountRoleName(CloudAccountType.Vault);

  const { setAccountId, accountId } = useCreateAwsAccount(
    CloudAccountType.Vault,
    () => {
      void mutate();
      onClose();
    }
  );
  const { validateAccount } = useValidateAccount();

  const onConnectClick = async () => {
    setIsDeploying(true);

    try {
      await dal.cloudAccounts.vault.create({
        cloudProvider: Provider.Aws,
        role: `arn:aws:iam::${accountId}:role/${roleName}`,
      });
      await mutate();
      onClose();
    } catch (error) {
      const errorMessage = await extractErrorMessage(error);
      setError(errorMessage);
    } finally {
      setIsDeploying(false);
    }
  };

  const openCloudFormation = () => {
    // once clicked once, we are enabling the connection button.
    setOpenCloudFormationClicked(true);
    void validateAccount(roleName, CloudAccountType.Vault);
  };

  return (
    <PanelWrapper
      rounded
      header={{
        title: 'Connect vault account',
        subtitle:
          'Before you start, make sure you can create and manage roles in your AWS environment',
        onClose,
      }}
      footer={{
        primaryButton: {
          'data-testid': 'submit-connect-vault-account',
          text: 'Connect',
          onClick: onConnectClick,
          disabled: !openCloudFormationClicked || isDeploying,
        },
        children: error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : null,
      }}
    >
      <Box
        paddingY='min(60px, 4vw)'
        paddingX='min(100px, 6vw)'
        width='100%'
        height='auto'
      >
        <Stack
          width='100%'
          gap='min(60px, 4vw)'
          divider={<Divider sx={{ borderStyle: 'dashed' }} />}
        >
          <NumericBulletSection
            bulletNumber={1}
            title={`Set connection details`}
          >
            <BulletItem
              leftContent={
                <Stack gap='24px'>
                  <FormTextField
                    title='Account ID'
                    placeholder={accountIdConfig.placeholder}
                    validation={accountIdConfig.validation}
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                  />
                  <FormTextField
                    title='Eon role name'
                    placeholder='Enter role name'
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                  />
                </Stack>
              }
            />
          </NumericBulletSection>
          <NumericBulletSection bulletNumber={2} title={`Review and deploy`}>
            <Stack width='540px' gap='24px'>
              <Typography variant='body1'>
                Launch CloudFormation to review and deploy Eon’s permissions in
                your AWS account. Afterward, come back to this tab to finish the
                connection.
              </Typography>
              <ButtonWithIcon
                sx={{ width: '250px' }}
                disabled={!accountId || !roleName}
                variant='contained'
                color='primary'
                text='Launch CloudFormation in AWS'
                icon='material-symbols-open-in-new'
                iconPosition='right'
                onClick={openCloudFormation}
              />
            </Stack>
          </NumericBulletSection>
        </Stack>
      </Box>
    </PanelWrapper>
  );
};
